import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Save, ArrowLeft } from "lucide-react";
import { supabase } from "../lib/supabase";
import SEO from "./SEO";
import * as Icons from "lucide-react";

const INITIAL_FORM_STATE = {
  type: "ai_training",
  header: "",
  description: "",
  button_text: "",
  icon: "ExternalLink",
  is_active: true,
  modal_type: null as "calendar" | "contact" | null,
};

export default function AdminCTAEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCTA();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchCTA = async () => {
    try {
      const { data, error } = await supabase
        .from("ctas")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (data) setFormData(data);
    } catch (err) {
      console.error("Error fetching CTA:", err);
      alert("Failed to load CTA");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);

      if (id) {
        const { error } = await supabase
          .from("ctas")
          .update(formData)
          .eq("id", id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("ctas").insert([formData]);

        if (error) throw error;
      }

      navigate("/admin/ctas");
    } catch (err) {
      console.error("Error saving CTA:", err);
      alert("Failed to save CTA");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SEO
        title={`One80Labs - ${id ? "Edit" : "New"} CTA`}
        description="Create or edit a call-to-action component in the One80Labs content management system."
        noindex={true}
      />
      <div className="flex justify-between items-center">
        <Link
          to="/admin/ctas"
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to CTAs
        </Link>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? "Saving..." : "Save CTA"}
        </button>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value as any })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              >
                <option value="ai_training">AI Training</option>
                <option value="agent_dev">Agent Development</option>
                <option value="fractional_pm">Fractional PM</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Modal Type
              </label>
              <select
                value={formData.modal_type || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    modal_type: e.target.value as "calendar" | "contact" | null,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              >
                <option value="">None (Regular Link)</option>
                <option value="calendar">Calendar Modal</option>
                <option value="contact">Contact Modal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Header
              </label>
              <input
                type="text"
                value={formData.header}
                onChange={(e) =>
                  setFormData({ ...formData, header: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Button Text
              </label>
              <input
                type="text"
                value={formData.button_text}
                onChange={(e) =>
                  setFormData({ ...formData, button_text: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Icon
              </label>
              <select
                value={formData.icon}
                onChange={(e) =>
                  setFormData({ ...formData, icon: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              >
                {Object.keys(Icons).map((iconName) => (
                  <option key={iconName} value={iconName}>
                    {iconName}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
              />
              <label
                htmlFor="is_active"
                className="ml-2 block text-sm text-gray-900"
              >
                Active
              </label>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
